import React, { createContext, useState, useCallback, useEffect, useContext } from 'react';
import { locales, loadLocale, defaultLanguage, fallbackLanguage, availableLanguages, languageNames } from '../locales';

// Context erstellen
export const LanguageContext = createContext(null);

// Custom Hook für einfachen Zugriff auf Übersetzungen
export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

// Hilfsfunktion: Verschachtelte Objekte durchsuchen mit Punkt-Notation
// z.B. "packages.name" -> locales.de.packages.name
const getNestedValue = (obj, path) => {
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return undefined;
    }
  }
  return result;
};

// Hilfsfunktion: Platzhalter ersetzen {{variable}}
const interpolate = (text, variables = {}) => {
  if (typeof text !== 'string') return text;
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
};

// Provider Komponente
export const LanguageProvider = ({ children }) => {
  // Sprache aus localStorage laden oder Browser-Sprache verwenden
  const [language, setLanguageState] = useState(() => {
    // Zuerst localStorage prüfen
    const stored = localStorage.getItem('app_language');
    if (stored && availableLanguages.includes(stored)) {
      return stored;
    }
    
    // Browser-Sprache prüfen
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && availableLanguages.includes(browserLang)) {
      return browserLang;
    }
    
    return defaultLanguage;
  });

  // Trackt ob die gewählte Sprachdatei bereits im Speicher liegt (für de/en sofort true)
  const [localeReady, setLocaleReady] = useState(() => !!locales[language]);

  useEffect(() => {
    let active = true;
    if (locales[language]) {
      setLocaleReady(true);
    } else {
      loadLocale(language).then(() => {
        if (active) setLocaleReady(true);
      });
    }
    return () => { active = false; };
  }, [language]);

  // Sprache ändern und in localStorage speichern
  const setLanguage = useCallback((lang) => {
    if (availableLanguages.includes(lang)) {
      localStorage.setItem('app_language', lang);
      if (locales[lang]) {
        setLanguageState(lang);
      } else {
        loadLocale(lang).then(() => {
          setLanguageState(lang);
        });
      }
    } else {
      console.warn(`Language "${lang}" is not available. Available: ${availableLanguages.join(', ')}`);
    }
  }, []);

  // 🧭 Konto-Profil hat die Sprache auf diesem Gerät geändert (Setup auf einem anderen Gerät) →
  // sofort übernehmen. Das Ereignis gibt es nur für Konten mit Profil.
  useEffect(() => {
    const onProfile = () => {
      const stored = localStorage.getItem('app_language');
      if (stored && availableLanguages.includes(stored)) {
        if (locales[stored]) {
          setLanguageState(stored);
        } else {
          loadLocale(stored).then(() => setLanguageState(stored));
        }
      }
    };
    window.addEventListener('kiosk:profile-applied', onProfile);
    return () => window.removeEventListener('kiosk:profile-applied', onProfile);
  }, []);

  // Übersetzungsfunktion
  const t = useCallback((key, variables = {}) => {
    // Zuerst in aktueller Sprache suchen
    let translation = getNestedValue(locales[language], key);
    
    // Fallback zur Fallback-Sprache
    if (translation === undefined && language !== fallbackLanguage) {
      translation = getNestedValue(locales[fallbackLanguage], key);
    }
    
    // Fallback zur Default-Sprache
    if (translation === undefined && language !== defaultLanguage) {
      translation = getNestedValue(locales[defaultLanguage], key);
    }
    
    // Wenn immer noch nicht gefunden, Key zurückgeben
    if (translation === undefined) {
      console.warn(`Translation missing for key: "${key}" in language "${language}"`);
      return key;
    }
    
    // Variablen interpolieren
    return interpolate(translation, variables);
  }, [language, localeReady]);

  // HTML lang-Attribut aktualisieren
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = {
    language,           // Aktuelle Sprache (z.B. 'de')
    setLanguage,        // Sprache ändern
    t,                  // Übersetzungsfunktion
    availableLanguages, // Liste aller Sprachen ['de', 'en', ...]
    languageNames,      // Namen der Sprachen { de: 'Deutsch', en: 'English' }
  };

  if (!localeReady && !locales[language]) {
    return null;
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
